import React, { Suspense } from 'react';
import { Amplify } from 'aws-amplify';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { awsConfig } from './aws-config';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './components/auth/LoginPage';
import { RegisterPage } from './components/auth/RegisterPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';
import { Dashboard } from './components/dashboard/Dashboard';
import { UploadPage } from './components/upload/UploadPage';
import { AnalyzePage } from './components/analyze/AnalyzePage';
import { ProfilePage } from './components/profile/ProfilePage';
import { AIConnectionsPage } from './components/connections/AIConnectionsPage';
import { UsagePage } from './components/usage/UsagePage';
import { AdminPage } from './components/admin/AdminPage';
import { IntrospectPage } from './components/introspect/IntrospectPage';
import { StatsDashboardPage } from './components/stats-dashboard/StatsDashboardPage';
import { IntegrationsPage } from './components/integrations/IntegrationsPage';
import { DeveloperApiPage } from './components/developer-api/DeveloperApiPage';
import { VisualizePage } from './components/visualize';
import { AccessControlPage } from './components/access-control/AccessControlPage';
import { ChatPage } from './components/chat/ChatPage';
import { SheetsPage } from './components/sheets/SheetsPage';
import { FlowsPage } from './components/flows/FlowsPage';
import { FlowDetailPage } from './components/flows/FlowDetailPage';
import { ThemeProvider } from './components/theme/theme-provider';
import { useMCPTokenRefresh } from './hooks/useMCPTokenRefresh';

// Configure AWS Amplify immediately when module loads (before any components render)
Amplify.configure(awsConfig);

/** True when running on the public demo subdomain */
const isDemoSite = window.location.hostname === 'demo.bigsplicer.com';

/** Lazy-loaded SheetsDemoWrapper — only downloaded on demo site */
const SheetsDemoWrapper = React.lazy(() =>
  import('./components/sheets/SheetsDemoWrapper').then((m) => ({ default: m.SheetsDemoWrapper }))
);

/** Lazy-loaded UploadDemoWrapper — only downloaded on demo site */
const UploadDemoWrapper = React.lazy(() =>
  import('./components/upload/UploadDemoWrapper').then((m) => ({ default: m.UploadDemoWrapper }))
);

/** Lazy-loaded LandingPage — interactive feature map, only downloaded on demo site */
const LandingPage = React.lazy(() =>
  import('./components/landing/LandingPage').then((m) => ({ default: m.LandingPage }))
);

function App() {
  // Automatically refresh MCP API key JWT token every 12 hours
  useMCPTokenRefresh();

  // Demo site: only show flow pages + auth pages, everything else redirects to /flows
  if (isDemoSite) {
    return (
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        enableSystem={false}
        disableTransitionOnChange={false}
      >
        <Router>
          <div className="App">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/flows" element={<FlowsPage />} />
              <Route path="/flows/:flowId" element={<FlowDetailPage />} />
              <Route
                path="/sheets"
                element={
                  <Suspense fallback={<div className="h-screen flex items-center justify-center bg-background"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>}>
                    <SheetsDemoWrapper>
                      <SheetsPage />
                    </SheetsDemoWrapper>
                  </Suspense>
                }
              />
              <Route
                path="/upload"
                element={
                  <Suspense fallback={<div className="h-screen flex items-center justify-center bg-background"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>}>
                    <UploadDemoWrapper>
                      <UploadPage />
                    </UploadDemoWrapper>
                  </Suspense>
                }
              />
              <Route path="/" element={<Suspense fallback={<div className="h-screen flex items-center justify-center bg-background"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>}><LandingPage /></Suspense>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </div>
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      disableTransitionOnChange={false}
    >
      <Router>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredFeature="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute>
                  <UploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analyze"
              element={
                <ProtectedRoute requiredFeature="analyze">
                  <AnalyzePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute requiredFeature="profile">
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-connections"
              element={
                <ProtectedRoute requiredFeature="ai-connections">
                  <AIConnectionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/usage"
              element={
                <ProtectedRoute requiredFeature="usage">
                  <UsagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/access-control"
              element={
                <ProtectedRoute requiredFeature="admin">
                  <AccessControlPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/introspect"
              element={
                <ProtectedRoute requiredFeature="introspect">
                  <IntrospectPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stats-dashboard"
              element={
                <ProtectedRoute requiredFeature="stats-dashboard">
                  <StatsDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute requiredFeature="integrations">
                  <IntegrationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer"
              element={
                <ProtectedRoute requiredFeature="developer-api">
                  <DeveloperApiPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/visualize"
              element={
                <ProtectedRoute requiredFeature="visualize">
                  <VisualizePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sheets"
              element={
                <ProtectedRoute requiredFeature="sheets">
                  <SheetsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/flows"
              element={
                <ProtectedRoute requiredFeature="flows">
                  <FlowsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/flows/:flowId"
              element={
                <ProtectedRoute requiredFeature="flows">
                  <FlowDetailPage />
                </ProtectedRoute>
              }
            />

            {/* Default route */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>

          {/* Toast notifications */}
          <Toaster position="top-right" richColors />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;